import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getCoach } from 'store/profile';

import { EventImageCodes, EventLevels, EventTypes } from 'defs/events';
import i18n from 'helper/i18n';
import appConfig from 'helper/mapBackendConfig';
import noop from 'helper/noop';
import ClientStorage, { StorageTypes } from 'lib/ClientStorage';
import { actionCreators as eventsAC } from 'store/events';
import { actionCreators as eventsActionCreators } from 'store/events';

import { AppStoreBadge, PlayStoreBadge } from 'components/fragments/StoreBadge';
import { isNativePlatform } from 'components/helper/Platform';

const AppStoreStorage = new ClientStorage(StorageTypes.LOCAL);
const appStoreHintDismissed = 'appStoreNotificationDismissed';
let notificationAlreadyVisible = false;

type AppStoreProps = {
	doAddEvents: typeof eventsActionCreators.doAddEvents;
	force?: boolean;
	debug?: boolean;
	coach: string;
};

export class AppStoreNotificationTrigger extends React.Component<AppStoreProps> {
	public static displayName = 'AppStoreNotificationTrigger';
	public static defaultProps = {
		force: false,
		doAddEvents: noop,
	};

	get notificationAlreadyVisible(): boolean {
		return notificationAlreadyVisible;
	}

	get supportsIOTransaction(): boolean {
		return AppStoreStorage.supportsIOTransaction;
	}

	get alreadyDismissedNotification(): boolean {
		return !!AppStoreStorage.get(appStoreHintDismissed);
	}

	get isOnNativeDevice(): boolean {
		return isNativePlatform();
	}

	public show() {
		const { doAddEvents, coach } = this.props;

		doAddEvents([
			{
				eventLevel: EventLevels.NotAvailable,
				code: EventImageCodes.AppStore,
				eventType: EventTypes.Image,
				callback: (eventCode: string) => {
					if (eventCode === 'unmount') {
						AppStoreStorage.set(appStoreHintDismissed, true);
						notificationAlreadyVisible = false;
					}
				},
				overwrites: {
					coach,
					title: i18n(`react.impulsCoach.events.image.appStore.na.title`),
					text: (
						<React.Fragment>
							<p>{i18n('react.impulsCoach.events.image.appStore.na.text')}</p>
							<div className="m-notification__store-badges">
								<AppStoreBadge
									imageClass="m-notification__store-img"
									linkClass="m-notification__store-link"
								/>
								<PlayStoreBadge
									imageClass="m-notification__store-img"
									linkClass="m-notification__store-link"
								/>
							</div>
						</React.Fragment>
					),
				},
			} as any,
		]);
	}

	public componentDidUpdate(): any {
		this.handleTrigger();
	}

	public componentDidMount(): any {
		this.handleTrigger();
	}

	public handleTrigger() {
		const { force, coach } = this.props;

		if (!appConfig.env.appLaunched) {
			return null;
		}

		if (!coach) {
			return null;
		}

		if (this.isOnNativeDevice) {
			return null;
		}

		if (this.notificationAlreadyVisible) {
			return null;
		}

		if (!this.supportsIOTransaction) {
			return null;
		}

		if (force) {
			notificationAlreadyVisible = true;
			return this.show();
		}

		if (this.alreadyDismissedNotification) {
			return null;
		}

		this.show();
		notificationAlreadyVisible = true;
	}

	public render() {
		return null;
	}
}

export const mapStateToProps = (state) => ({
	coach: getCoach(state),
});

export const mapDispatchToProps = (dispatch) => {
	return bindActionCreators(
		{
			doAddEvents: eventsAC.doAddEvents,
		},
		dispatch
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(AppStoreNotificationTrigger as any);
